import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditCard, User, MapPin } from "lucide-react";
import { EditableField } from "./EditableField";

/**
 * Credit Card Information Section for Rental Agreement
 */
export const CreditCardSection = ({ creditCardInfo, setCreditCardInfo, isEditing }) => {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-primary" />
          PAYMENT / CREDIT CARD INFORMATION
        </CardTitle>
        <CardDescription>Card on file for deposit and charges</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card Details */}
          <div className="space-y-3">
            <EditableField 
              label="Cardholder Name" 
              value={creditCardInfo.cardholderName} 
              isEditing={isEditing}
              onChange={(v) => setCreditCardInfo({...creditCardInfo, cardholderName: v})} 
              icon={<User className="w-3.5 h-3.5" />} 
            />
            <div className="grid grid-cols-2 gap-3">
              <EditableField 
                label="Card Number" 
                value={creditCardInfo.cardNumber} 
                isEditing={isEditing}
                onChange={(v) => setCreditCardInfo({...creditCardInfo, cardNumber: v})} 
                placeholder="****-****-****-1234" 
                testId="credit-card-number"
              />
              <div>
                <Label className="text-xs text-muted-foreground">Card Type</Label>
                {isEditing ? (
                  <Select 
                    value={creditCardInfo.cardType || ''} 
                    onValueChange={(v) => setCreditCardInfo({...creditCardInfo, cardType: v})}
                  >
                    <SelectTrigger className="h-8 mt-1" data-testid="card-type-selector">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Visa">Visa</SelectItem>
                      <SelectItem value="Mastercard">Mastercard</SelectItem>
                      <SelectItem value="Amex">American Express</SelectItem>
                      <SelectItem value="Discover">Discover</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-foreground mt-1">{creditCardInfo.cardType || '-'}</p>
                )}
              </div>
            </div>
            <EditableField 
              label="Expiry Date" 
              value={creditCardInfo.expiryDate} 
              isEditing={isEditing}
              onChange={(v) => setCreditCardInfo({...creditCardInfo, expiryDate: v})} 
              placeholder="MM/YY" 
              testId="credit-card-expiry"
            />
          </div>
          
          {/* Billing Address */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase">
              Billing Address
            </h4>
            <EditableField 
              label="Street Address" 
              value={creditCardInfo.billingAddress} 
              isEditing={isEditing}
              onChange={(v) => setCreditCardInfo({...creditCardInfo, billingAddress: v})} 
              icon={<MapPin className="w-3.5 h-3.5" />} 
            />
            {isEditing ? (
              <div className="flex gap-2">
                <Input 
                  value={creditCardInfo.billingCity || ''} 
                  onChange={(e) => setCreditCardInfo({...creditCardInfo, billingCity: e.target.value})} 
                  placeholder="City" 
                  className="h-8 text-sm" 
                  data-testid="billing-city"
                />
                <Input 
                  value={creditCardInfo.billingProvince || ''} 
                  onChange={(e) => setCreditCardInfo({...creditCardInfo, billingProvince: e.target.value})} 
                  placeholder="Province" 
                  className="h-8 text-sm w-24" 
                  data-testid="billing-province"
                />
                <Input 
                  value={creditCardInfo.billingPostalCode || ''} 
                  onChange={(e) => setCreditCardInfo({...creditCardInfo, billingPostalCode: e.target.value})} 
                  placeholder="Postal" 
                  className="h-8 text-sm w-24" 
                  data-testid="billing-postal-code"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {creditCardInfo.billingCity}
                {creditCardInfo.billingCity && creditCardInfo.billingProvince ? ', ' : ''}
                {creditCardInfo.billingProvince} {creditCardInfo.billingPostalCode}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreditCardSection;
